import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  signal,
  Signal,
  viewChild,
} from '@angular/core';
import {
  AbstractControl,
  NonNullableFormBuilder,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { first, Observable } from 'rxjs';

import { AuthService } from '@blaze-shared/auth-data-access';
import { DialogService } from '@blaze-shared/shared-feature';

import { isThemeNameInUse } from '../theme-naming/theme-naming.utils';

export interface ThemeEditorSaveDialogData {
  willApplyNewTheme: boolean;
  /** The name proposed for the new theme, already free of collisions with `usedNames`. */
  name: string;
  usedNames: string[];
  save: (name: string, saveAsBlazeTheme: boolean) => Observable<void>;
}

@Component({
  selector: 'blaze-theme-editor-save-dialog',
  templateUrl: './theme-editor-save-dialog.component.html',
  styleUrls: ['./theme-editor-save-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ThemeEditorSaveDialogComponent implements AfterViewInit {
  private readonly data = inject<ThemeEditorSaveDialogData>(MAT_DIALOG_DATA);
  private readonly authService = inject(AuthService);
  private readonly dialogRef = inject<MatDialogRef<ThemeEditorSaveDialogComponent>>(MatDialogRef);
  private readonly dialogService = inject(DialogService);
  private readonly fb = inject(NonNullableFormBuilder);

  private readonly nameInput = viewChild.required<ElementRef<HTMLInputElement>>('nameInput');

  form = this.fb.group({
    name: [this.data.name, [Validators.required, isUniqueNameValidator(this.data.usedNames)]],
    saveAsBlazeTheme: [false],
  });

  readonly canSaveAsBlazeTheme: Signal<boolean>;
  readonly loading = signal(false);
  readonly willApplyNewTheme: Signal<boolean>;

  get isNameInUse(): boolean {
    return this.form.controls.name.hasError('unique');
  }

  constructor() {
    this.willApplyNewTheme = signal(this.data.willApplyNewTheme);
    this.canSaveAsBlazeTheme = signal(this.authService.isCurrentUserAccountRootOrDev());
  }

  ngAfterViewInit(): void {
    const input = this.nameInput().nativeElement;

    input.focus();
    input.select();
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  confirm(): void {
    if (!this.form.valid) {
      return;
    }

    const { saveAsBlazeTheme } = this.form.getRawValue();

    this.loading.set(true);

    if (saveAsBlazeTheme) {
      this.dialogService
        .openConfirmDialog({
          title: 'Confirm creation of a new default theme version',
          description: 'Are you sure you want to create a new default theme version?',
          cancelLabel: 'Cancel',
          confirmLabel: 'Create',
        })
        .afterClosed()
        .subscribe((confirmed) => {
          if (confirmed) {
            this.save();
          } else {
            this.loading.set(false);
          }
        });
    } else {
      this.save();
    }
  }

  private save(): void {
    const { name, saveAsBlazeTheme } = this.form.getRawValue();

    this.data
      .save(name, saveAsBlazeTheme)
      .pipe(first())
      .subscribe({
        next: () => {
          this.dialogRef.close();
        },
        error: () => {
          this.loading.set(false);
        },
      });
  }
}

function isUniqueNameValidator(usedNames: string[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null =>
    isThemeNameInUse(control.value ?? '', usedNames) ? { unique: true } : null;
}
